'use client';
import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { Settings, Trophy, Zap, Users, Shield, Star, Palette } from 'lucide-react';


const GameCanvas = dynamic(() => import('./components/GameCanvas'), { ssr: false });

// ── 30 unique organism styles ──
const ORGANISM_STYLES = [
  { color: '#00f5ff', secondary: '#0066ff', name: 'Luma Prime', nucleus: '#ffffff', aura: 'rgba(0,245,255,0.3)', type: 'clean' },
  { color: '#ff00ff', secondary: '#bf00ff', name: 'Magenta Wraith', nucleus: '#ffaaff', aura: 'rgba(255,0,255,0.3)', type: 'corrupted' },
  { color: '#39ff14', secondary: '#00aa00', name: 'Toxic Bloom', nucleus: '#ccff88', aura: 'rgba(57,255,20,0.35)', type: 'toxic' },
  { color: '#ffff00', secondary: '#ff9900', name: 'Solar Flare', nucleus: '#ffffff', aura: 'rgba(255,255,0,0.3)', type: 'clean' },
  { color: '#ff6600', secondary: '#ff3300', name: 'Lava Core', nucleus: '#ffcc88', aura: 'rgba(255,102,0,0.35)', type: 'boss' },
  { color: '#bf00ff', secondary: '#6600cc', name: 'Void Pulse', nucleus: '#ddaaff', aura: 'rgba(191,0,255,0.3)', type: 'corrupted' },
  { color: '#0066ff', secondary: '#003399', name: 'Deep Surge', nucleus: '#aaccff', aura: 'rgba(0,102,255,0.3)', type: 'clean' },
  { color: '#ff0040', secondary: '#cc0022', name: 'Blood Spore', nucleus: '#ffaaaa', aura: 'rgba(255,0,64,0.3)', type: 'corrupted' },
  { color: '#00ffaa', secondary: '#00cc77', name: 'Neon Drift', nucleus: '#aaffdd', aura: 'rgba(0,255,170,0.3)', type: 'clean' },
  { color: '#ff44aa', secondary: '#cc0077', name: 'Plasma Wisp', nucleus: '#ffccee', aura: 'rgba(255,68,170,0.3)', type: 'clean' },
  { color: '#44ffff', secondary: '#00aacc', name: 'Cryo Cell', nucleus: '#ccffff', aura: 'rgba(68,255,255,0.35)', type: 'clean' },
  { color: '#ff8800', secondary: '#cc5500', name: 'Ember Husk', nucleus: '#ffddaa', aura: 'rgba(255,136,0,0.3)', type: 'boss' },
  { color: '#aa00ff', secondary: '#7700cc', name: 'Glitch Shade', nucleus: '#ddaaff', aura: 'rgba(170,0,255,0.35)', type: 'corrupted' },
  { color: '#00ff66', secondary: '#00cc44', name: 'Acid Bloom', nucleus: '#aaffcc', aura: 'rgba(0,255,102,0.3)', type: 'toxic' },
  { color: '#ff2200', secondary: '#aa0000', name: 'Ruin Herald', nucleus: '#ffaaaa', aura: 'rgba(255,34,0,0.35)', type: 'boss' },
  { color: '#6600ff', secondary: '#4400cc', name: 'Entropy Shard', nucleus: '#ccaaff', aura: 'rgba(102,0,255,0.3)', type: 'corrupted' },
  { color: '#ffcc00', secondary: '#cc9900', name: 'Radiant Orb', nucleus: '#ffffff', aura: 'rgba(255,204,0,0.3)', type: 'clean' },
  { color: '#00ccff', secondary: '#0088cc', name: 'Hydro Pulse', nucleus: '#aaeeff', aura: 'rgba(0,204,255,0.3)', type: 'clean' },
  { color: '#ff0088', secondary: '#cc0055', name: 'Decay Bloom', nucleus: '#ffaad4', aura: 'rgba(255,0,136,0.3)', type: 'corrupted' },
  { color: '#88ff00', secondary: '#55cc00', name: 'Spore Burst', nucleus: '#ddffaa', aura: 'rgba(136,255,0,0.3)', type: 'toxic' },
  { color: '#0044ff', secondary: '#002299', name: 'Void Surge', nucleus: '#aabbff', aura: 'rgba(0,68,255,0.3)', type: 'clean' },
  { color: '#ff5500', secondary: '#cc3300', name: 'Waste Titan', nucleus: '#ffccaa', aura: 'rgba(255,85,0,0.35)', type: 'boss' },
  { color: '#00ff44', secondary: '#00cc22', name: 'Blight Core', nucleus: '#aaffbb', aura: 'rgba(0,255,68,0.3)', type: 'toxic' },
  { color: '#cc00ff', secondary: '#8800cc', name: 'Chaos Nexus', nucleus: '#eeaaff', aura: 'rgba(204,0,255,0.35)', type: 'corrupted' },
  { color: '#ffaa00', secondary: '#cc7700', name: 'Amber Drift', nucleus: '#ffeebb', aura: 'rgba(255,170,0,0.3)', type: 'clean' },
  { color: '#00ffff', secondary: '#00cccc', name: 'Aqua Wraith', nucleus: '#ccffff', aura: 'rgba(0,255,255,0.3)', type: 'clean' },
  { color: '#ff3366', secondary: '#cc1144', name: 'Plague Omega', nucleus: '#ffaabb', aura: 'rgba(255,51,102,0.3)', type: 'boss' },
  { color: '#33ff99', secondary: '#00cc66', name: 'Neon Leaker', nucleus: '#bbffdd', aura: 'rgba(51,255,153,0.3)', type: 'toxic' },
  { color: '#9900ff', secondary: '#6600aa', name: 'Dark Matter', nucleus: '#ddaaff', aura: 'rgba(153,0,255,0.35)', type: 'corrupted' },
  { color: '#ff99ff', secondary: '#cc66cc', name: 'Spectral Haze', nucleus: '#ffffff', aura: 'rgba(255,153,255,0.3)', type: 'clean' },
];

const TYPE_BADGE: Record<string, { label: string; color: string }> = {
  clean: { label: 'CLEAN', color: 'rgba(0,245,255,0.7)' },
  corrupted: { label: 'CORRUPTED', color: 'rgba(191,0,255,0.7)' },
  toxic: { label: 'TOXIC', color: 'rgba(57,255,20,0.7)' },
  boss: { label: 'BOSS', color: 'rgba(255,102,0,0.7)' },
};

// Mock top-10 leaderboard data
const MOCK_LEADERBOARD = [
  { rank: 1, name: 'WASTE-TITAN', score: 2847392, size: 892, isBot: true },
  { rank: 2, name: 'CORRUPTED-Ω', score: 2341087, size: 743, isBot: true },
  { rank: 3, name: 'Vexor', score: 1923441, size: 612, isBot: true },
  { rank: 4, name: 'GLITCH-LORD', score: 1654230, size: 534, isBot: true },
  { rank: 5, name: 'Zyth', score: 1432100, size: 478, isBot: true },
  { rank: 6, name: 'TOXIC-APEX', score: 1287654, size: 421, isBot: true },
  { rank: 7, name: 'Krell', score: 1098432, size: 387, isBot: true },
  { rank: 8, name: 'NEON-WRAITH', score: 987654, size: 356, isBot: true },
  { rank: 9, name: 'Pulse', score: 876543, size: 312, isBot: true },
  { rank: 10, name: 'VOID-CRUSHER', score: 765432, size: 289, isBot: true },
];

function formatScore(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

// Canvas-based organism cell renderer
function OrganismCell({ style, size = 52 }: { style: typeof ORGANISM_STYLES[0]; size?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2, cy = size / 2;
    const r = size * 0.38;

    const draw = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const t = (ts - startRef.current) / 1000;
      ctx.clearRect(0, 0, size, size);

      const pulse = 1 + Math.sin(t * 2.5) * 0.06;
      const pr = r * pulse;

      // Aura
      const aura = ctx.createRadialGradient(cx, cy, pr * 0.5, cx, cy, pr * 1.9);
      aura.addColorStop(0, style.aura);
      aura.addColorStop(1, 'transparent');
      ctx.fillStyle = aura;
      ctx.beginPath(); ctx.arc(cx, cy, pr * 1.9, 0, Math.PI * 2); ctx.fill();

      // Glow
      ctx.shadowColor = style.color;
      ctx.shadowBlur = 10 + Math.sin(t * 3) * 4;

      // Body
      const grad = ctx.createRadialGradient(cx - pr * 0.3, cy - pr * 0.3, pr * 0.05, cx, cy, pr);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.25, style.color);
      grad.addColorStop(0.7, style.secondary);
      grad.addColorStop(1, style.color + '60');
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(cx, cy, pr, 0, Math.PI * 2); ctx.fill();

      // Shell cracks for boss/corrupted
      if (style.type === 'boss' || style.type === 'corrupted') {
        ctx.shadowBlur = 0;
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth = 1.2;
        for (let c = 0; c < 4; c++) {
          const angle = (c / 4) * Math.PI * 2 + t * 0.1;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(angle) * pr * 0.3, cy + Math.sin(angle) * pr * 0.3);
          ctx.lineTo(cx + Math.cos(angle) * pr * 0.85, cy + Math.sin(angle) * pr * 0.85);
          ctx.stroke();
        }
        ctx.strokeStyle = style.color + '80';
        ctx.lineWidth = 0.5;
        for (let c = 0; c < 4; c++) {
          const angle = (c / 4) * Math.PI * 2 + t * 0.1;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(angle) * pr * 0.3, cy + Math.sin(angle) * pr * 0.3);
          ctx.lineTo(cx + Math.cos(angle) * pr * 0.85, cy + Math.sin(angle) * pr * 0.85);
          ctx.stroke();
        }
      }

      // Nucleus
      ctx.shadowBlur = 0;
      ctx.fillStyle = style.nucleus;
      ctx.beginPath(); ctx.arc(cx, cy, pr * 0.28, 0, Math.PI * 2); ctx.fill();

      // Toxic ring for toxic type
      if (style.type === 'toxic') {
        ctx.strokeStyle = style.color + 'aa';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.arc(cx, cy, pr * 1.2 + Math.sin(t * 2) * 2, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [style, size]);

  return <canvas ref={canvasRef} width={size} height={size} style={{ display: 'block' }} />;
}

export default function PlayPage() {
  const [showGame, setShowGame] = useState(false);
  const [hoveredOrganism, setHoveredOrganism] = useState<number | null>(null);

  if (showGame) {
    return (
      <div className="fixed inset-0 bg-background overflow-hidden">
        <GameCanvas />
        <Link
          href="/settings"
          className="fixed top-3 right-3 z-50 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-mono font-bold transition-all duration-200 hover:scale-105 active:scale-95 min-h-[44px]"
          style={{
            background: 'rgba(5,5,20,0.82)',
            border: '1px solid rgba(0,245,255,0.25)',
            color: 'rgba(0,245,255,0.85)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 0 12px rgba(0,245,255,0.1)',
          }}
        >
          <Settings size={14} />
          <span className="hidden sm:inline">Settings</span>
        </Link>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{ background: 'linear-gradient(180deg, #020810 0%, #050a18 50%, #030610 100%)' }}
    >
      {/* Neon background glow blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #00f5ff, transparent)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #bf00ff, transparent)', filter: 'blur(60px)' }} />
        <div className="absolute top-2/3 left-1/2 w-64 h-64 rounded-full opacity-4" style={{ background: 'radial-gradient(circle, #39ff14, transparent)', filter: 'blur(50px)' }} />
      </div>

      {/* Nav */}
      <nav
        className="sticky top-0 z-50 flex items-center justify-between px-4 py-3"
        style={{
          background: 'rgba(2,8,16,0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(0,245,255,0.08)',
        }}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'radial-gradient(circle, #00f5ff, #0066ff)', boxShadow: '0 0 12px rgba(0,245,255,0.5)' }}>
            <Zap size={14} className="text-white" />
          </div>
          <span className="font-mono font-bold text-sm" style={{ color: '#00f5ff', textShadow: '0 0 10px rgba(0,245,255,0.5)' }}>
            LUNORA ARENA
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Link href="/live-arena" className="text-xs font-mono text-gray-500 hover:text-cyan-400 transition-colors px-2 py-1.5 rounded min-h-[36px] flex items-center">Live</Link>
          <Link href="/weekly-leaderboard" className="text-xs font-mono text-gray-500 hover:text-cyan-400 transition-colors px-2 py-1.5 rounded min-h-[36px] flex items-center">Weekly</Link>
          <Link href="/customization" className="flex items-center gap-1 text-xs font-mono text-gray-500 hover:text-purple-400 transition-colors px-2 py-1.5 rounded min-h-[36px]">
            <Palette size={12} />
            <span className="hidden sm:inline">Skins</span>
          </Link>
          <Link href="/settings" className="flex items-center gap-1 text-xs font-mono text-gray-500 hover:text-cyan-400 transition-colors px-2 py-1.5 rounded min-h-[36px]">
            <Settings size={12} />
            <span className="hidden sm:inline">Settings</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-12">

        {/* ── HERO ── */}
        <div className="text-center space-y-4 pt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono mb-2" style={{ background: 'rgba(0,245,255,0.06)', border: '1px solid rgba(0,245,255,0.15)', color: 'rgba(0,245,255,0.7)' }}>
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            POST-APOCALYPTIC NEON ORGANISM ARENA
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black font-mono tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #00f5ff 0%, #bf00ff 50%, #39ff14 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
              filter: 'drop-shadow(0 0 20px rgba(0,245,255,0.3))',
            }}
          >
            LUNORA NEON CELLS
          </h1>
          <p className="text-sm text-gray-400 font-mono max-w-md mx-auto leading-relaxed">
            Grow your Luma Cell. Absorb thousands of neon particles. Dominate the AI waste arena.
          </p>
          <button
            onClick={() => setShowGame(true)}
            className="mt-4 px-8 py-3.5 rounded-xl font-mono font-black text-base transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(0,245,255,0.15), rgba(0,102,255,0.15))',
              border: '1px solid rgba(0,245,255,0.4)',
              color: '#00f5ff',
              boxShadow: '0 0 30px rgba(0,245,255,0.2), inset 0 0 20px rgba(0,245,255,0.05)',
            }}
          >
            ▶ ENTER ARENA
          </button>
        </div>

        {/* ── TOP 10 LEADERBOARD + STATS ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Live Leaderboard */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(5,10,25,0.8)',
              border: '1px solid rgba(0,245,255,0.12)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: '1px solid rgba(0,245,255,0.08)' }}
            >
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-yellow-400" />
                <span className="text-xs font-mono font-bold text-gray-200 tracking-widest uppercase">Top 10 Leaders</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                <span className="text-[10px] font-mono text-gray-600">Preview Data</span>
              </div>
            </div>

            <div className="p-2 space-y-0.5">
              {MOCK_LEADERBOARD.map((entry) => {
                const rankColor =
                  entry.rank === 1 ? '#ffd700' :
                  entry.rank === 2 ? '#c0c0c0' :
                  entry.rank === 3 ? '#cd7f32': 'rgba(120,120,140,0.6)';

                return (
                  <div
                    key={entry.rank}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                    style={{
                      background: entry.rank <= 3 ? 'rgba(255,215,0,0.04)' : 'rgba(255,255,255,0.02)',
                    }}
                  >
                    <span className="font-mono font-black text-xs w-5 text-right shrink-0" style={{ color: rankColor }}>
                      {entry.rank}
                    </span>
                    <div
                      className="w-4 h-4 rounded-full shrink-0"
                      style={{
                        background: `radial-gradient(circle, ${ORGANISM_STYLES[(entry.rank - 1) % ORGANISM_STYLES.length].color}, ${ORGANISM_STYLES[(entry.rank - 1) % ORGANISM_STYLES.length].secondary})`,
                        boxShadow: `0 0 6px ${ORGANISM_STYLES[(entry.rank - 1) % ORGANISM_STYLES.length].color}60`,
                      }}
                    />
                    <span className="font-mono text-xs flex-1 truncate" style={{ color: entry.rank <= 3 ? '#e0e0f0' : 'rgba(160,160,180,0.7)' }}>
                      {entry.name}
                    </span>
                    <span className="font-mono text-[10px] text-gray-500 shrink-0">{formatScore(entry.score)}</span>
                    <span className="font-mono text-[10px] text-gray-600 shrink-0 hidden sm:block">sz:{entry.size}</span>
                  </div>
                );
              })}
            </div>

            <div
              className="px-4 py-2.5 text-center"
              style={{ borderTop: '1px solid rgba(0,245,255,0.06)' }}
            >
              <span className="text-[10px] font-mono text-gray-600">
                Live arena not connected yet · Preview data shown
              </span>
            </div>
          </div>

          {/* Arena Stats */}
          <div className="space-y-3">
            {[
              { icon: <Users size={16} className="text-cyan-400" />, label: 'Arena Population', value: '5,500+', sub: 'AI organisms active', color: 'rgba(0,245,255,0.08)', border: 'rgba(0,245,255,0.15)' },
              { icon: <Zap size={16} className="text-yellow-400" />, label: 'Energy Particles', value: '14,000+', sub: 'Neon dots across the map', color: 'rgba(255,255,0,0.05)', border: 'rgba(255,255,0,0.12)' },
              { icon: <Shield size={16} className="text-orange-400" />, label: 'Mini-Bosses', value: '40', sub: 'Corrupted wandering titans', color: 'rgba(255,102,0,0.06)', border: 'rgba(255,102,0,0.15)' },
              { icon: <Star size={16} className="text-purple-400" />, label: 'World Size', value: '40,000²', sub: 'Massive endless arena', color: 'rgba(191,0,255,0.06)', border: 'rgba(191,0,255,0.15)' },
            ].map((stat, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: stat.color, border: `1px solid ${stat.border}` }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  {stat.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{stat.label}</div>
                  <div className="text-lg font-black font-mono text-gray-100">{stat.value}</div>
                </div>
                <div className="text-[10px] font-mono text-gray-600 text-right hidden sm:block">{stat.sub}</div>
              </div>
            ))}

            <div className="grid grid-cols-3 gap-2">
              {[
                { label: '6 Mobile', sub: 'Control modes', color: '#00f5ff' },
                { label: '3 Desktop', sub: 'Control modes', color: '#bf00ff' },
                { label: 'Gamepad', sub: 'Analog support', color: '#39ff14' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="rounded-xl p-3 text-center"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <div className="text-sm font-black font-mono" style={{ color: item.color }}>{item.label}</div>
                  <div className="text-[9px] font-mono text-gray-600 mt-0.5">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 30 ORGANISM GALLERY ── */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-black font-mono tracking-widest uppercase" style={{ color: '#00f5ff' }}>
                Organism Gallery
              </h2>
              <p className="text-xs text-gray-500 font-mono mt-0.5">30 unique glowing cell styles in the arena</p>
            </div>
            <div className="flex items-center gap-2">
              {Object.entries(TYPE_BADGE).map(([type, badge]) => (
                <div key={type} className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: badge.color }} />
                  <span className="text-[9px] font-mono hidden sm:block" style={{ color: badge.color }}>{badge.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
            {ORGANISM_STYLES.map((style, i) => {
              const badge = TYPE_BADGE[style.type];
              const isHovered = hoveredOrganism === i;
              return (
                <div
                  key={i}
                  className="relative flex flex-col items-center gap-1 cursor-pointer transition-transform duration-150"
                  style={{ transform: isHovered ? 'scale(1.12)' : 'scale(1)' }}
                  onMouseEnter={() => setHoveredOrganism(i)}
                  onMouseLeave={() => setHoveredOrganism(null)}
                >
                  <div
                    className="rounded-full overflow-hidden"
                    style={{
                      background: 'rgba(5,10,25,0.6)',
                      border: `1px solid ${style.color}40`,
                      boxShadow: isHovered ? `0 0 16px ${style.color}50` : 'none',
                      transition: 'box-shadow 0.15s',
                    }}
                  >
                    <OrganismCell style={style} size={52} />
                  </div>
                  {isHovered && (
                    <div
                      className="absolute -bottom-8 left-1/2 z-10 whitespace-nowrap px-2 py-1 rounded text-[9px] font-mono pointer-events-none"
                      style={{
                        transform: 'translateX(-50%)',
                        background: 'rgba(5,10,25,0.95)',
                        border: `1px solid ${style.color}50`,
                        color: style.color,
                      }}
                    >
                      {style.name}
                    </div>
                  )}
                  <div
                    className="w-1 h-1 rounded-full"
                    style={{ background: badge.color }}
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-center">
            <p className="text-[10px] font-mono text-gray-600">
              Hover to see organism names · Colors, nuclei, halos, auras, and corruption patterns vary
            </p>
          </div>
        </section>

        {/* ── QUICK LINKS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-8">
          {[
            { href: '/live-arena', label: 'Live Arena', sub: 'Real-time stats', color: '#00f5ff' },
            { href: '/weekly-leaderboard', label: 'Weekly Board', sub: 'Top scores', color: '#ffff00' },
            { href: '/customization', label: 'Skins', sub: '50+ Luma Cell skins', color: '#bf00ff' },
            { href: '/settings', label: 'Settings', sub: 'Controls & options', color: '#39ff14' },
          ].map((link, i) => (
            <Link
              key={i}
              href={link.href}
              className="flex flex-col items-center gap-1 py-4 rounded-xl transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${link.color}20`,
              }}
            >
              <span className="text-sm font-bold font-mono" style={{ color: link.color }}>{link.label}</span>
              <span className="text-[10px] font-mono text-gray-600">{link.sub}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}